/**
 * Copyright 2023-2026 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { useMemo } from "react";
import dayjs from "dayjs";
import { Else, If, Then } from "react-if";
import styled from "styled-components";
import {
  ExtendedTooltip,
  FlexContainer,
  Table,
  Text,
  theme,
} from "@nordcloud/gnui";
import {
  ResourceExecutionStatus,
  useEventResourceStatusChangedSubscription,
} from "~/generated/graphql";
import { StyledSVGIcon } from "~/components/Styled/StyledSVGIcon";
import { StyledTd } from "~/components/Tables/styles";
import { dateFormat } from "~/constants";
import { ROUTES } from "~/routing/routes";
import { isNotNil } from "~/tools";
import { hasLogRelatedErrorCode } from "~/utils/logErrorCode";
import { EventResourceGroupAction } from "~/views/events/EventsDetails/Tabs/Details/components/ActionBox/components/EventResourceGroupAction";
import type { EventResourceState } from "~/views/events/EventsDetails/Tabs/Details/hooks/useEventResourceController/types";
import { OSCell } from "~/views/resources/components/OSCell";
import { NameWithLink } from "../Tables/cells/NameWithLink";
import {
  getAttentionNeededDescription,
  getResourceStatusConfig,
  getSuccessDetailedStatusConfig,
  hasSuccessfulStatus,
} from "./utils";

type Props = {
  state: EventResourceState;
  eventId: string;
  eventActionId: string;
};

export function EventResourceStateItem({
  state,
  eventId,
  eventActionId,
}: Props) {
  const { data } = useEventResourceStatusChangedSubscription({
    variables: {
      input: {
        id: state.id,
      },
    },
  });

  const os = {
    subType: state.resource?.os?.subType ?? "",
    type: state.resource?.os?.type ?? "",
  };

  const resourceStatus = data?.eventResourceStatusChanged ?? state.status;

  const isExecutionInProgress =
    resourceStatus === ResourceExecutionStatus.InProgress ||
    resourceStatus === ResourceExecutionStatus.Pending;

  const changeStatusDetails = {
    eventId: eventId,
    eventActionId: eventActionId,
    eventActionResourceStateId: state.id,
  };

  const isManualStatus =
    resourceStatus === ResourceExecutionStatus.ManualSuccess;

  const isAttentionNeeded =
    hasSuccessfulStatus(resourceStatus) &&
    hasLogRelatedErrorCode(state.errorCode);

  const statusConfig = useMemo(() => {
    const baseConfig = getResourceStatusConfig(resourceStatus);

    if (!isAttentionNeeded) {
      return baseConfig;
    }

    return getSuccessDetailedStatusConfig(baseConfig);
  }, [resourceStatus, isAttentionNeeded]);

  const statusDescription = isAttentionNeeded
    ? getAttentionNeededDescription(statusConfig.description)
    : statusConfig.description;

  return (
    <Table.tr key={state.resource?.id ?? state.resourceId}>
      <StyledTd css={{ width: "2%" }}>
        <ExtendedTooltip
          caption={
            <If condition={isManualStatus}>
              <Then>
                <TooltipOverlay>
                  <div>Status:</div> <span>{statusDescription}</span>
                  <div>Changes made:</div>
                  <span>
                    {dayjs(state.changeDetails.updatedAt).format(
                      dateFormat.dayMonthYear
                    )}
                  </span>
                  <div>Changed by:</div>
                  <span>{state.changeDetails.updatedBy}</span>
                  <div>Comment:</div>
                  <span>{state.annotation}</span>
                </TooltipOverlay>
              </Then>
              <Else>{statusDescription}</Else>
            </If>
          }
          status={statusConfig.tooltipColor}
          position="start"
        >
          <StyledSVGIcon
            name={statusConfig.icon}
            css={{ minWidth: theme.spacing.spacing04 }}
            color={statusConfig.color()}
            pathColors={statusConfig.pathColors}
          />
        </ExtendedTooltip>
      </StyledTd>
      <StyledTd css={{ width: "2%" }}>
        <OSCell os={os} />
      </StyledTd>
      <Table.td>
        <FlexContainer direction="column" alignItems="flex-start">
          <If condition={isNotNil(state.resource)}>
            <Then>
              <NameWithLink
                name={state.resource?.name ?? state.resourceId}
                link={`${ROUTES.resources.generateDetails(
                  state.resource?.id ?? ""
                )}`}
              />
              <Text
                css={{ wordBreak: "break-word" }}
                size="sm"
                color={theme.color.text.text02}
              >
                {state.resourceId}
              </Text>
            </Then>
            <Else>
              <ExtendedTooltip
                caption={"Resource has been deleted"}
                position="start"
              >
                <Text size="md" color={theme.color.text.text02}>
                  {state.resourceId}
                </Text>
              </ExtendedTooltip>
            </Else>
          </If>
        </FlexContainer>
      </Table.td>
      <StyledTd css={{ textAlign: "right" }}>
        <EventResourceGroupAction
          actionOutputUrl={state.outputUri}
          resourceName={state.resource?.name ?? state.resourceId}
          changeStatusDetails={changeStatusDetails}
          isInProgress={isExecutionInProgress}
        />
      </StyledTd>
    </Table.tr>
  );
}

const TooltipOverlay = styled.div`
  display: grid;
  grid-template-columns: 5.5rem 1fr;
  span {
    text-overflow: ellipsis;
    overflow: hidden;
  }
`;
